// ⏳ LDR — el arranque: telón, línea de progreso y contador. El mismo gesto
// que en 0101sanity.
//
// Dos fases. initLDR() corre el contador mientras core🫀.js monta la primera
// vista (en paralelo). endLDR() lo cierra, y ahí está el gesto:
//
//   EL TELÓN NO SUBE, SE CONVIERTE EN EL RAIL. Es --sk-frame, el mismo negro
//   que la barra lateral, así que se ENCOGE HACIA LA IZQUIERDA (--x, el
//   clip-path de ⏳LDR.pcss) hasta medir justo --side-w y ahí ya es el rail.
//   Mientras se encoge, el rail se puebla: logo y botones entran escalonados,
//   y la barra de herramientas y el contenido del panel (.CTN) suben desde
//   abajo a la vez.
//
// CORRE UNA SOLA VEZ, en el boot. Las navegaciones del browser no lo repiten:
// para entonces el loader ya no está en el DOM.
//
// Duraciones en SEGUNDOS (engine.timeUnit = 's' en 🪇ANM.js).

import './⏳LDR.pcss'
import { animate, createTimeline, stagger, utils, swiftOut as ease } from '@anm/🪇ANM.js'

export function mountLDR() {
  if (document.querySelector('.LDR')) return
  const el = document.createElement('div')
  el.className = 'LDR'
  el.id = 'loader'
  el.setAttribute('aria-hidden', 'true')
  el.innerHTML = `<div class="LDR_prg"></div><div class="LDR_cnt"><span>0</span><span>%</span></div>`
  document.body.appendChild(el)
}

export function initLDR() {
  return new Promise(resolve => {
    const el = document.querySelector('.LDR')
    if (!el) { resolve(null); return }

    // El .waiter tapa el primer frame sin CSS; en cuanto hay estilos, fuera.
    const waiter = document.querySelector('.waiter')
    if (waiter) {
      animate(waiter, {
        opacity: 0,
        duration: .2,
        delay: .01,
        onComplete: () => waiter.remove(),
      })
    }

    const prg = el.querySelector('.LDR_prg')
    const cnt = el.querySelector('.LDR_cnt span:first-child')
    const dur = 1

    const ANMcounter = createTimeline()
      .add(cnt, {
        innerHTML: '100',
        modifier: utils.roundPad(0),
        duration: dur,
        ease,
      }, 0)
      .add(prg, {
        scaleX: [0, .3, .8, 1],
        duration: dur,
        ease,
      }, 0)
      .init()

    // Se frena mientras la vista carga; endLDR() la acelera al cerrar.
    const obj = { speed: 1 }
    animate(obj, {
      speed: .3,
      duration: 1.5,
      delay: .8,
      ease,
      onUpdate: () => { ANMcounter.speed = obj.speed },
    })

    setTimeout(() => resolve(ANMcounter), (dur + .1) * 1000)
  })
}

export function endLDR(ANMcounter) {
  return new Promise(resolve => {
    const el = document.querySelector('.LDR')
    if (!el) { resolve(); return }

    const rail = [
      ...document.querySelectorAll('.SBR_logo, .SBR_links .SBR_btn:not([hidden]), .SBR_foot .SBR_btn:not([hidden])'),
    ]
    const bar = document.querySelector('.NAV_bar')
    const ctn = document.querySelector('.CTN')
    const entran = [...rail, bar, ctn].filter(Boolean)

    // Con «reducir movimiento» no hay coreografía: el telón desaparece y todo
    // nace en su sitio, sin haber estado nunca a opacidad 0.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (ANMcounter) ANMcounter.pause()
      el.remove()
      resolve()
      return
    }

    if (ANMcounter) {
      const obj = { speed: ANMcounter.speed }
      ANMcounter.play()
      animate(obj, {
        speed: 5,
        duration: .5,
        onUpdate: () => { ANMcounter.speed = obj.speed },
      })
    }

    // El estado de partida se pone AQUÍ y no en la propia animación: a los
    // .18 s el telón ya ha descubierto un 12 % del panel por la derecha, así
    // que .CTN a opacidad 1 asomaría un instante antes de saltar a 0.
    entran.forEach(n => { n.style.opacity = '0' })

    // Y el rastro se limpia al final. anime deja transform y opacity inline:
    // en un .SBR_btn el transform se comería su :active {scale(.94)}, y en
    // .CTN es peor — un ancestro con transform pasa a ser el bloque contenedor
    // de todo position:fixed que lleve dentro.
    const limpia = () => entran.forEach(n => {
      n.style.transform = ''
      n.style.opacity = ''
      n.style.willChange = ''
    })

    const tl = createTimeline({
      onComplete: () => { limpia(); resolve() },
    })

    tl.add(el, {
      '--x': 1,
      duration: .55,
      ease,
      // El telón se retira EN CUANTO llega al ancho del rail, no al final de
      // la coreografía: los últimos botones terminan de entrar ya a la vista.
      onComplete: () => el.remove(),
    }, 0)

    tl.add('.LDR_cnt', { y: -6, opacity: 0, duration: .2, ease }, 0)

    if (rail.length) {
      tl.add(rail, {
        y: [6, 0],
        opacity: [0, 1],
        duration: .35,
        delay: stagger(.045),
        ease,
      }, .18)
    }

    if (bar) {
      tl.add(bar, { y: [-6, 0], opacity: [0, 1], duration: .4, ease }, .2)
    }

    if (ctn) {
      tl.add(ctn, { y: [10, 0], opacity: [0, 1], duration: .45, ease }, .18)
    }
  })
}
